import { AnnotationMeta } from './AnnotationMeta.js';
import { AnnotationPluginKey, findAnnotationPosition } from './AnnotationPlugin.js';
import { DecorationSet, Decoration } from '@tiptap/pm/view';
import { isTruthy, isString } from '@fabric-app/utils/guards';

class AnnotationState {
  constructor(params) {
    this.hoveredAnnotationId = null;
    this.focusedAnnotationId = null;
    this.decorationSet = DecorationSet.empty;
    this.annotationRange = null;
    this.commentId = null;
    this.selectionBased = false;
    Object.assign(this, params);
  }
  static get empty() {
    return new AnnotationState({
      hoveredAnnotationId: null,
      focusedAnnotationId: null,
      commentId: null,
      decorationSet: DecorationSet.empty,
      annotationRange: null,
      selectionBased: false
    });
  }
  update(params) {
    return new AnnotationState({
      ...this,
      ...params
    });
  }
  handleMeta(tr) {
    const meta = tr.getMeta(AnnotationPluginKey);
    if (!meta || !(meta instanceof AnnotationMeta)) return this;
    if (meta.hoveredAnnotationId === this.hoveredAnnotationId && meta.focusedAnnotationId === this.focusedAnnotationId) return this;
    return AnnotationState.empty.update({
      hoveredAnnotationId: meta.hoveredAnnotationId,
      focusedAnnotationId: meta.focusedAnnotationId,
      commentId: meta.commentId,
      selectionBased: false
    });
  }
  updateDecorations(tr) {
    if (!this.hoveredAnnotationId && !this.focusedAnnotationId) {
      return this.update({
        decorationSet: DecorationSet.empty
      });
    }
    if (!tr.docChanged && this.annotationRange && !this.focusedAnnotationId) return this;
    const position = this.hoveredAnnotationId ? findAnnotationPosition(tr.doc, {
      annotationId: this.hoveredAnnotationId
    }) : null;
    const focusPosition = this.focusedAnnotationId ? findAnnotationPosition(tr.doc, {
      annotationId: this.focusedAnnotationId
    }) : null;
    if (!position && !focusPosition) {
      return this.update({
        decorationSet: DecorationSet.empty,
        annotationRange: null
      });
    }
    const hoveredDecoration = position && this.hoveredAnnotationId ? Decoration.inline(position.start, position.end, {
      ["data-annotation-hovered"]: "true",
      ["data-annotation-id"]: this.hoveredAnnotationId
    }) : null;
    const focusedDecoration = focusPosition && this.focusedAnnotationId ? Decoration.inline(focusPosition.start, focusPosition.end, {
      ["data-focus"]: "true",
      ["data-annotation-id"]: this.focusedAnnotationId
    }) : null;
    const decorationSet = DecorationSet.create(tr.doc, [
      hoveredDecoration,
      focusedDecoration
    ].filter(isTruthy));
    return this.update({
      decorationSet,
      annotationRange: position ? {
        from: position.start,
        to: position.end
      } : null
    });
  }
  handleSelection(tr) {
    if (!tr.selectionSet) return this;
    const { selection } = tr;
    const { from, to, $from } = selection;
    if (from !== to) return this.selectionBased ? AnnotationState.empty : this;
    const node = $from.parent.maybeChild($from.index());
    if (!node) return this.selectionBased ? AnnotationState.empty : this;
    const annotation = node.marks.find((mark) => mark.type.name === "annotation");
    if (!annotation) return this.selectionBased ? AnnotationState.empty : this;
    const annotationId = annotation.attrs.annotationId;
    const commentId = annotation.attrs.commentId;
    if (!isString(annotationId)) return this.selectionBased ? AnnotationState.empty : this;
    if (annotationId === this.hoveredAnnotationId) return this;
    return AnnotationState.empty.update({
      hoveredAnnotationId: annotationId,
      commentId,
      selectionBased: true
    });
  }
}

export { AnnotationState };
//# sourceMappingURL=AnnotationState.js.map
